import { PublishMethods } from '@wix/fe-essentials-viewer-platform/bi'
import type { BaseFactory, FetchFn } from '@wix/thunderbolt-symbols'
import type { FactoryOptions } from './types'
import { getActivationStart } from './biPrerenderUtils'

function calculateTSN({
	initialRequestTimestamp,
	adjustForPrerender = false,
}: {
	initialRequestTimestamp: number
	adjustForPrerender?: boolean
}) {
	if (!process.env.browser) {
		// SSR - no performance api until we move to node 19
		return Math.round(Date.now() - initialRequestTimestamp)
	}
	if (typeof window === 'undefined') {
		// Worker - now is from worker start so we need to add the main thread diff
		return Math.round(performance.now() + (performance.timeOrigin - initialRequestTimestamp))
	}
	// browser - main thread: adjust for prerender if experiment is enabled
	const activationOffset = adjustForPrerender ? getActivationStart() : 0
	return Math.round(performance.now() - activationOffset)
}

/**
 * Base BI logger factory, should be used as a basis for any BI logger in the Viewer
 *
 * - Initialized with base defaults (which are supported globally in the BI schema).
 * - Any additional defaults should be added via specialized factories, like FedOps,
 *   and only after making sure the BI schema supports them.
 *
 * Please use #bi-logger-support for any questions
 */

const createBaseBiLoggerFactory = ({
	useBatch = true,
	publishMethod = process.env.browser ? PublishMethods.Auto : PublishMethods.Fetch,
	endpoint,
	muteBi = process.env.browser ? false : true,
	biStore,
	sessionManager,
	fetch,
	factory,
}: FactoryOptions): BaseFactory => {
	const biLoggerFactory = factory({
		useBatch,
		publishMethod,
		endpoint,
	})
		.setMuted(muteBi)
		.withUoUContext({ msid: biStore.msid })
		.withNonEssentialContext({
			// @ts-expect-error - @antonp kindly fix types
			visitorId: () => sessionManager.getVisitorId(),
			// @ts-expect-error - @antonp kindly fix types
			siteMemberId: () => sessionManager.getSiteMemberId(),
		})
		.updateDefaults({
			vsi: biStore.viewerSessionId,
			_av: `thunderbolt-${biStore.viewerVersion}`,
			isb: biStore.is_headless,
			...(biStore.is_headless && { isbr: biStore.is_headless_reason }),
		})

	if (!process.env.browser) {
		const fetchWithProtocol: FetchFn = (url, options) => fetch(`https:${url}`, options)
		biLoggerFactory.withPublishFunction({
			[PublishMethods.Fetch]: fetchWithProtocol,
		})
	}

	return biLoggerFactory
}

/**
 * BI logger factory for FedOps
 *
 * - Initialized with base defaults + defaults which are supported by FedOps BI events
 *   https://bo.wix.com/bi-catalog-webapp/#/sources/72
 *
 * - Any additional defaults should be added only after making sure the BI schema supports them
 *
 * Please use #bi-logger-support for any questions
 */
const createBiLoggerFactoryForFedops = (options: FactoryOptions) => {
	const {
		biStore: {
			session_id,
			initialTimestamp,
			initialRequestTimestamp,
			dc,
			microPop,
			is_headless,
			isCached,
			pageData,
			rolloutData,
			caching,
			checkVisibility = () => '',
			viewerVersion,
			requestUrl,
			st,
			isSuccessfulSSR,
			mpaSessionId,
		},
		muteBi = false,
	} = options

	return createBaseBiLoggerFactory({ ...options, muteBi }).updateDefaults({
		ts: () => Date.now() - initialTimestamp,
		// this computation is worker compatible cause performance.now() in the worker is computed from worker initialization time
		// adjustForPrerender handles prerendered pages where performance.now() starts from prerender, not activation
		tsn: () => calculateTSN({ initialRequestTimestamp, adjustForPrerender: true }),
		dc,
		microPop,
		caching,
		session_id,
		st,
		url: requestUrl || pageData.pageUrl,
		ish: is_headless,
		pn: pageData.pageNumber,
		isFirstNavigation: pageData.pageNumber === 1,
		pv: checkVisibility,
		pageId: pageData.pageId,
		isServerSide: !process.env.browser,
		isSuccessfulSSR,
		is_lightbox: pageData.isLightbox,
		is_cached: isCached,
		is_sav_rollout: rolloutData.siteAssetsVersionsRollout ? 1 : 0,
		is_dac_rollout: rolloutData.isDACRollout ? 1 : 0,
		v: viewerVersion,
		mpaSessionId,
		...(process.env.browser &&
			typeof document !== 'undefined' &&
			document.referrer && { document_referrer: document.referrer }),
		...(process.env.browser &&
			typeof navigator !== 'undefined' &&
			navigator.language && { browserLanguage: navigator.language }),
	})
}

export const commonBiLoggerFactory = {
	createBaseBiLoggerFactory,
	createBiLoggerFactoryForFedops,
}
